import React from "react";
import { motion, useIsPresent } from "framer-motion";
import "./Modal.scss";

interface ModalProps {
  children: React.ReactNode;
  handleClose?: () => void;
  // heading at the top of the body (an <h3>)
  title?: React.ReactNode;
  // extra class on the body, next to .modal-body (e.g. "edit-item-modal")
  className?: string;
  // skip the .modal-body wrapper (and title) — for content that brings its own
  // layout, like the full-bleed receipt photo
  bare?: boolean;
  // seconds for the closing animation; the press-and-hold receipt preview
  // wants this near-instant
  exitDuration?: number;
}

// Backdrop fades and the card pops in (90% -> 100%) on open, and reverses on
// close. The close animation only plays when the caller renders the modal
// inside <AnimatePresence> (e.g. {open && <Modal …/>}), which keeps it mounted
// until the exit finishes.
const Modal: React.FC<ModalProps> = ({
  children,
  handleClose = () => {},
  title,
  className,
  bare = false,
  exitDuration = 0.2,
}) => {
  // while closing, let taps fall through to the page instead of hitting the
  // fading backdrop
  const isPresent = useIsPresent();

  return (
    <motion.div
      className="modal-backdrop"
      style={{ pointerEvents: isPresent ? "auto" : "none" }}
      onClick={handleClose}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.25 } }}
      exit={{ opacity: 0, transition: { duration: exitDuration } }}
    >
      <motion.div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{
          opacity: 1,
          scale: 1,
          transition: { duration: 0.25, ease: "easeOut" },
        }}
        exit={{
          opacity: 0,
          scale: 0.9,
          transition: { duration: exitDuration, ease: "easeIn" },
        }}
      >
        {bare ? (
          children
        ) : (
          <div className={`modal-body${className ? ` ${className}` : ""}`}>
            {title && <h3>{title}</h3>}
            {children}
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

export default Modal;
